import React, { useEffect, useMemo, useState } from 'react';
import investorApi from '/src/apis/investorApi';
import Pagination from '/src/components/Pagination/Pagination';
import FundsRaisedSummaryCards from './Components/FundsRaisedSummaryCards/FundsRaisedSummaryCards';
import LpCommitmentsModal from './Components/LpCommitmentsModal/LpCommitmentsModal';
import FundsRaisedTableGrid from './Components/FundsRaisedTableGrid/FundsRaisedTableGrid';
import { DEFAULT_PAGE_SIZE, LIMIT_OPTIONS, SORT_API_FIELD, MANAGED_FUNDS_TABLE_FIELDS } from './constants';
import {
    buildManagedFundRow,
    computeFundSummaryStatsFromAggregation,
    pinBalanceSheetLast,
} from './helpers';

// ─── Main component ───────────────────────────────────────────────────────────
const FundsRaised = ({ entityId, entityType, investorName = '', hasDirectoryAccess = false, onFundClick = null }) => {
    const [sortKey, setSortKey] = useState('vintage');
    const [sortDir, setSortDir] = useState('desc');
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(DEFAULT_PAGE_SIZE);
    const [tableRows, setTableRows] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [initialFetchDone, setInitialFetchDone] = useState(false);
    const [aggregation, setAggregation] = useState({});
    const [lpModalRow, setLpModalRow] = useState(null);

    // Summary stats derived from the aggregation API
    const summaryStats = useMemo(
        () => computeFundSummaryStatsFromAggregation(aggregation),
        [aggregation]
    );

    // ── Fetch one page of funds ───────────────────────────────────────────────
    const fetchFundsPage = async (currentPage, currentLimit, currentSortKey, currentSortDir) => {
        setIsLoading(true);
        setHasError(false);
        try {
            const apiField = SORT_API_FIELD[currentSortKey];
            const sortParam = apiField ? `${apiField}^:${currentSortDir.toUpperCase()}` : null;
            const fields = currentSortKey === 'lpCount'
                ? [...MANAGED_FUNDS_TABLE_FIELDS, 'investors_count']
                : MANAGED_FUNDS_TABLE_FIELDS;
            const fundRes = await investorApi.getFundsForInvestor(
                entityId, entityType, fields, currentLimit, currentPage, sortParam
            );

            // API resolves with a bare [] (not the {data, navigation} shape) on error
            if (!fundRes || !Array.isArray(fundRes.data)) {
                setTableRows([]);
                setHasError(true);
                return;
            }

            const funds = fundRes.data;
            setTotalRecords(fundRes.navigation?.total || 0);
            setTableRows(pinBalanceSheetLast(funds.map((fund) => buildManagedFundRow(fund))));
        } catch (e) {
            setTableRows([]);
            setHasError(true);
        } finally {
            setIsLoading(false);
            setInitialFetchDone(true);
        }
    };

    // Fetch aggregation for summary cards on mount
    useEffect(() => {
        if (entityId && entityType) {
            investorApi
                .getManagedFundAggregation(entityId, entityType)
                .then((data) => setAggregation(data || {}))
                .catch(() => setAggregation({}));
        }
    }, [entityId, entityType]);

    // Fetch page 1 on mount and Re-fetch from page 1 when sort changes
    useEffect(() => {
        if (entityId && entityType) {
            setPage(1);
            fetchFundsPage(1, limit, sortKey, sortDir);
        }
    }, [entityId, entityType ,sortKey, sortDir]);

    // ── Pagination wiring ─────────────────────────────────────────────────────
    // Pagination calls setParentState({ page: N, isLoading: true })
    // then after the parent re-renders with the new page prop it fires fetchData().
    const updateState = (newState) => {
        if (newState.limit !== undefined) setLimit(newState.limit);
        if (newState.isLoading !== undefined) setIsLoading(newState.isLoading);
        // limit change always resets to page 1; plain page change uses the given value
        const nextPage = newState.limit !== undefined ? 1 : newState.page;
        if (nextPage !== undefined) setPage(nextPage);
    };

    // ── Client-side sort on the current page ──────────────────────────────────
    const handleSort = (key) => {
        if (sortKey === key) {
            setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortKey(key);
            setSortDir('asc');
        }
    };

    if (!initialFetchDone) return null;

    return (
        <div className="funds-raised-wrapper funding-box">
            {/* ── Headline ── */}
            {investorName && (
                <p className="fr-headline">
                    <span className="fr-headline__name">{investorName}</span>
                    {' manages '}
                    <strong>{summaryStats.totalFunds}</strong>
                    {` fund${summaryStats.totalFunds !== 1 ? 's' : ''}`}
                    {summaryStats.totalCapitalRaised && (
                        <>
                            {' with '}
                            <span className={`fr-headline__amount${summaryStats.totalCapitalRaisedHasProxy ? ' amount--estimated' : ''}`}>
                                {summaryStats.totalCapitalRaised}
                            </span>
                            {' in total capital raised'}
                        </>
                    )}
                </p>
            )}

            {/* ── Summary cards ── */}
            <FundsRaisedSummaryCards stats={summaryStats} />

            {/* ── Table ── */}
            <FundsRaisedTableGrid
                rows={tableRows}
                isLoading={isLoading}
                hasError={hasError}
                sortKey={sortKey}
                sortDir={sortDir}
                onSort={handleSort}
                onLpClick={setLpModalRow}
                onFundClick={onFundClick}
            />

            {/* ── LP Commitments modal ── */}
            <LpCommitmentsModal
                isOpen={lpModalRow !== null}
                onClose={() => setLpModalRow(null)}
                fundName={lpModalRow?.name}
                lpRows={lpModalRow?.lpRows || []}
                hasDirectoryAccess={hasDirectoryAccess}
            />

            {/* ── Pagination — show whenever totalRecords exceeds the minimum page size so the component stays mounted regardless of the selected limit ── */}
            {totalRecords > LIMIT_OPTIONS[0] && (
                <div className="funding-table-pagination" data-table-type="managedFunds">
                    <Pagination
                        page={page}
                        totalRecords={totalRecords}
                        limit={limit}
                        limitOptions={LIMIT_OPTIONS}
                        setParentState={updateState}
                        fetchData={() => fetchFundsPage(page, limit, sortKey, sortDir)}
                        isLoading={isLoading}
                        tableName="managed-funds"
                    />
                </div>
            )}
        </div>
    );
};

export default FundsRaised;
